import { Component, OnInit } from '@angular/core';
import {init, SupportedLanguages} from 'onfido-sdk-ui';
import {OnfidoService} from '../services/onfido.service';
import {ActivatedRoute} from '@angular/router';

@Component({
  selector: 'app-onfido',
  templateUrl: './onfido.component.html',
  styleUrls: ['./onfido.component.css']
})
export class OnfidoComponent implements OnInit {

  public protectedOrderId: string;

  constructor(
    private onfidoService: OnfidoService,
    private route: ActivatedRoute,
  ) { }

  getPhrases(lang: string): any {
    let obj = {
      country_select: {
        alert_dropdown: {
          country_not_found: 'Country not found'
        },
        alert: {
          another_doc: 'Documents from that country are not currently supported — <fallback>try another document type<\/fallback>'
        },
        button_primary: 'Submit document',
        search: {
          accessibility: 'Select country',
          input_placeholder: 'e.g. United States',
          label: 'Search for country'
        },
        title: 'Select issuing country'
      },
      cross_device_checklist: {
        button_primary: 'Submit verification',
        info: 'Tips',
        list_item_doc_multiple: 'Documents uploaded',
        list_item_doc_one: 'Document uploaded',
        list_item_selfie: 'Selfie uploaded',
        list_item_video: 'Video uploaded',
        subtitle: 'We\'re now ready to verify your identity',
        title: 'Great, that\'s everything we need'
      },
      cross_device_error_desktop: {
        subtitle: 'The link only works on mobile devices',
        title: 'Something\'s gone wrong'
      },
      cross_device_error_restart: {
        subtitle: 'You\'ll need to restart your verification on your computer',
        title: 'Something\'s gone wrong'
      },
      cross_device_intro: {
        button_primary: 'Get secure link',
        list_accessibility: 'Steps required to continue verification on your mobile',
        list_item_finish: 'Check back here to finish the submission',
        list_item_open_link: 'Open the link and complete the tasks',
        list_item_send_phone: 'Send a secure link to your phone',
        subtitle: 'Here\'s how to do it:',
        title: 'Continue on your phone'
      },
      cross_device_return: {
        body: 'Your computer may take a few seconds to update',
        subtitle: 'You can now return to your computer to continue',
        title: 'Uploads successful'
      },
      doc_confirmation: {
        alert: {
          blur_detail: 'Make sure everything is clear',
          blur_title: 'Blurry photo detected',
          crop_detail: 'Make sure full document is visible',
          crop_title: 'Cut-off image detected',
          glare_detail: 'Move away from direct light',
          glare_title: 'Glare detected',
          no_doc_detail: 'Make sure all of the document is in the photo',
          no_doc_title: 'No document detected'
        },
        body_bank_statement: 'Make sure details are clear to read, with no blur or glare',
        body_benefits_letter: 'Make sure details are clear to read, with no blur or glare',
        body_bill: 'Make sure details are clear to read, with no blur or glare',
        body_id: 'Make sure your card details are clear to read, with no blur or glare',
        body_image_medium: 'It’ll take longer to verify you if we can’t read it',
        body_image_poor: 'To smoothly verify you, we need a better photo',
        body_license: 'Make sure your license details are clear to read, with no blur or glare',
        body_passport: 'Make sure your passport details are clear to read, with no blur or glare',
        body_permit: 'Make sure your permit details are clear to read, with no blur or glare',
        body_tax_letter: 'Make sure details are clear to read, with no blur or glare',
        button_close: 'Close',
        button_primary_redo: 'Redo',
        button_primary_upload: 'Confirm',
        button_primary_upload_anyway: 'Upload anyway',
        button_secondary_redo: 'Redo',
        button_zoom: 'Enlarge image',
        image_accessibility: 'Photo of your document',
        title: 'Check your image'
      },
      doc_select: {
        button_bank_statement: 'Bank or building society statement',
        button_benefits_letter: 'Benefits Letter',
        button_benefits_letter_detail: 'Government authorised household benefits eg. Jobseeker allowance, Housing benefit, Tax credits',
        button_bill: 'Utility Bill',
        button_bill_detail: 'Gas, electricity, water, landline, or broadband',
        button_government_letter: 'Government Letter',
        button_government_letter_detail: 'Any government issued letter eg. Benefits entitlement, Voting letters, Tax letters, etc',
        button_id: 'Identity card',
        button_id_detail: 'Front and back',
        button_license: 'Driver\'s license',
        button_license_detail: 'Front and back',
        button_passport: 'Passport',
        button_passport_detail: 'Face photo page',
        button_permit: 'Residence permit',
        button_permit_detail: 'Front and back',
        button_tax_letter: 'Council Tax Letter',
        extra_estatements_ok: 'e-statements accepted',
        extra_no_mobile: 'Sorry, no mobile phone bills',
        list_accessibility: 'Documents you can use to verify your identity',
        subtitle: 'It must be an official photo ID',
        subtitle_poa: 'These are the documents most likely to show your current home address',
        title: 'Choose document',
        title_poa: 'Select a %{country} document'
      },
      doc_submit: {
        button_link_upload: 'or upload photo – no scans or photocopies',
        button_primary: 'Continue on phone',
        subtitle: 'Take a photo with your phone',
        title_bank_statement: 'Submit statement',
        title_benefits_letter: 'Submit letter',
        title_bill: 'Submit bill',
        title_government_letter: 'Government Letter',
        title_id_back: 'Submit identity card (back)',
        title_id_front: 'Submit identity card (front)',
        title_license_back: 'Submit license (back)',
        title_license_front: 'Submit license (front)',
        title_passport: 'Submit passport photo page',
        title_permit_back: 'Submit residence permit (back)',
        title_permit_front: 'Submit residence permit (front)',
        title_tax_letter: 'Submit letter'
      },
      error_unsupported_browser: {
        subtitle_android: 'Restart the process on the latest version of Google Chrome',
        subtitle_ios: 'Restart the process on the latest version of Safari',
        title_android: 'Unsupported browser',
        title_ios: 'Unsupported browser'
      },
      generic: {
        accessibility: {
          close_sdk_screen: 'Close identity verification screen',
          dismiss_alert: 'Dismiss alert'
        },
        back: 'back',
        close: 'close',
        errors: {
          interrupted_flow_error: {
            instruction: 'Restart process on a different device',
            message: 'Camera not detected'
          },
          invalid_size: {
            instruction: 'Must be under 10MB.',
            message: 'File size exceeded.'
          },
          invalid_type: {
            instruction: 'Try using another file type.',
            message: 'File not uploaded.'
          },
          lazy_loading: {
            message: 'An error occurred while loading the component'
          },
          multiple_faces: {
            instruction: 'Only your face can be in the selfie',
            message: 'Multiple faces found'
          },
          no_face: {
            instruction: 'Your face is needed in the selfie',
            message: 'No face found'
          },
          request_error: {
            instruction: 'Please try again',
            message: 'Connection lost'
          },
          sms_failed: {
            instruction: 'Copy the link to your phone',
            message: 'Something\'s gone wrong'
          },
          sms_overuse: {
            instruction: 'Copy the link to your phone',
            message: 'Too many failed attempts'
          },
          unsupported_file: {
            instruction: 'Try using a JPG or PNG file',
            message: 'File type not supported'
          }
        },
        lazy_load_placeholder: 'Loading...',
        loading: 'Loading'
      },
      get_link: {
        alert_wrong_number: 'Check that your number is correct',
        button_copied: 'Copied',
        button_copy: 'Copy',
        button_submit: 'Send link',
        info_qr_how: 'How to scan a QR code',
        info_qr_how_list_item_camera: 'Point your phone’s camera at the QR code',
        info_qr_how_list_item_download: 'If it doesn’t work, download a QR code scanner from Google Play or the App Store',
        link_divider: 'or',
        link_qr: 'Scan QR code',
        link_sms: 'Get link via SMS',
        link_url: 'Copy link',
        loader_sending: 'Sending',
        number_field_input_placeholder: 'Enter mobile number',
        number_field_label: 'Enter your mobile number:',
        subtitle_qr: 'Scan the QR code with your phone',
        subtitle_sms: 'Send this one-time link to your phone',
        subtitle_url: 'Open the link on your mobile',
        title: 'Get your secure link',
        url_field_label: 'Copy the link to your mobile browser'
      },
      linked_computer: {
        button_primary: 'Continue',
        info: 'Make sure',
        list_item_desktop_open: '2. Your desktop window stays open',
        list_item_sent_by_you: '1. This link was sent by you',
        subtitle: 'Continue with the verification',
        title: 'Linked to your computer'
      },
      mobilePhrases: {
        photo_upload: {
          body_id_back: 'Take a photo of the back of your card',
          body_id_front: 'Take a photo of the front of your card',
          body_license_back: 'Take a photo of the back of your license',
          body_license_front: 'Take a photo of the front of your license',
          body_passport: 'Take a photo of your passport photo page',
          body_selfie: 'Take a selfie showing your face'
        },
        selfie_capture: {
          alert: {
            camera_inactive: {
              detail: 'Take a photo using the <fallback>basic camera mode<\/fallback> instead'
            },
            camera_not_working: {
              detail: 'Take a photo using the <fallback>basic camera mode<\/fallback> instead'
            }
          }
        },
        upload_guide: {
          button_primary: 'Take a photo',
          title: 'Passport photo page'
        }
      },
      outro: {
        body: 'Thank you',
        title: 'Verification complete'
      },
      permission_recovery: {
        button_primary: 'Refresh',
        info: 'Recovery',
        list_header_cam: 'Follow these steps to recover camera access:',
        list_item_action_cam: 'Refresh this page to restart the identity verification process',
        list_item_how_to_cam: 'Grant access to your camera from your browser settings',
        subtitle_cam: 'Recover camera access to continue face verification',
        title_cam: 'Camera access is denied'
      },
      permission: {
        body_cam: 'We cannot verify you without using your camera',
        button_primary_cam: 'Enable camera',
        subtitle_cam: 'When prompted, you must enable camera access to continue',
        title_cam: 'Allow camera access'
      },
      photo_upload: {
        body_bank_statement: 'Provide the whole document page for best results',
        body_benefits_letter: 'Provide the whole document page for best results',
        body_bill: 'Provide the whole document page for best results',
        body_government_letter: 'Provide the whole document page for best results',
        body_id_back: 'Upload back of card from your computer',
        body_id_front: 'Upload front of card from your computer',
        body_license_back: 'Upload back of license from your computer',
        body_license_front: 'Upload front of license from your computer',
        body_passport: 'Upload passport photo page from your computer',
        body_selfie: 'Upload a selfie from your computer',
        body_tax_letter: 'Provide the whole document page for best results',
        button_take_photo: 'Take photo',
        button_upload: 'Upload',
        title_selfie: 'Selfie'
      },
      poa_guidance: {
        button_primary: 'Continue',
        instructions: {
          address: 'Current address',
          full_name: 'Full name',
          issue_date: 'Issue date or summary period',
          label: 'Make sure it clearly shows:',
          logo: 'Logo'
        },
        subtitle_bank_statement: 'Must be issued in the <strong>last 3 months<\/strong>',
        subtitle_benefits_letter: 'Must be issued in the <strong>last 12 months<\/strong>',
        subtitle_bill: 'Must be issued in the <strong>last 3 months<\/strong>',
        subtitle_government_letter: 'Must be issued in the <strong>last 12 months<\/strong>',
        subtitle_tax_letter: 'Must be issued in the <strong>last 12 months<\/strong>'
      },
      poa_intro: {
        button_primary: 'Start verification',
        list_matches_signup: '<strong>Matches<\/strong> the address you used on signup',
        list_most_recent: 'Is your most <strong>recent<\/strong> document',
        list_shows_address: 'Shows your <strong>current<\/strong> address',
        subtitle: 'You\'ll need a document that:',
        title: 'Let\'s verify your %{country} address'
      },
      selfie_capture: {
        alert: {
          camera_inactive: {
            detail: 'Check that it is connected and functional. You can also <fallback>continue verification on your phone<\/fallback>',
            detail_no_fallback: 'Make sure your device has a working camera',
            title: 'Camera not working?'
          },
          camera_not_working: {
            detail: 'It may be disconnected. <fallback>Try using your phone instead<\/fallback>.',
            detail_no_fallback: 'Make sure your device\'s camera works',
            title: 'Camera not working'
          },
          timeout: {
            detail: 'Remember to press stop when you\'re done. <fallback>Redo video actions<\/fallback>',
            title: 'Looks like you took too long'
          }
        },
        button_accessibility: 'Take a photo',
        frame_accessibility: 'View from camera',
        title: 'Take a selfie'
      },
      selfie_confirmation: {
        image_accessibility: 'Photo of your face',
        subtitle: 'Make sure your selfie clearly shows your face',
        title: 'Check selfie'
      },
      selfie_intro: {
        button_primary: 'Continue',
        list_accessibility: 'Tips to take a good selfie',
        list_item_face_forward: 'Face forward and make sure your eyes are clearly visible',
        list_item_no_glasses: 'Remove your glasses, if necessary',
        subtitle: 'We\'ll compare it with your document',
        title: 'Take a selfie'
      },
      sms_sent: {
        info: 'Tips',
        info_link_expire: 'Your link will expire in one hour',
        info_link_window: 'Keep this window open while using your mobile',
        link: 'Resend link',
        subtitle: 'We\'ve sent a secure link to %{number}',
        subtitle_minutes: 'It may take a few minutes to arrive',
        title: 'Check your mobile'
      },
      switch_phone: {
        info: 'Tips',
        info_link_expire: 'Your mobile link will expire in one hour',
        info_link_refresh: 'Don\'t refresh this page',
        info_link_window: 'Keep this window open while using your mobile',
        link: 'Cancel',
        subtitle: 'Once you\'ve finished we\'ll take you to the next step',
        title: 'Connected to your mobile'
      },
      upload_guide: {
        button_primary: 'Upload photo',
        image_detail_blur_alt: 'Example of a blurry document',
        image_detail_blur_label: 'All details must be clear — nothing blurry',
        image_detail_cutoff_alt: 'Example of a cut-off document',
        image_detail_cutoff_label: 'Show all details — including the bottom 2 lines',
        image_detail_glare_alt: 'Example of a document with glare',
        image_detail_glare_label: 'Move away from direct light — no glare',
        image_detail_good_alt: 'Document example',
        image_detail_good_label: 'The photo should clearly show your document',
        subtitle: 'Scans and photocopies are not accepted',
        title: 'Upload passport photo page'
      },
      user_consent: {
        button_primary: 'Accept',
        button_secondary: 'Do not accept',
        prompt: {
          button_primary: 'Review again',
          button_secondary: 'Yes, don’t verify me',
          no_consent_detail: 'If you do not accept Onfido’s privacy statements and terms of service, we will not be able to verify your identity and you will exit this step.',
          no_consent_title: 'Are you sure?'
        }
      },
      video_capture: {
        body: 'Position your face in the oval',
        body_next: 'When you\'re done, press next',
        body_record: 'Press record and follow the instructions',
        body_stop: 'When you\'re done, press stop',
        button_primary_next: 'Next',
        button_record_accessibility: 'Start recording',
        button_stop_accessibility: 'Stop recording',
        frame_accessibility: 'View from camera',
        header: {
          challenge_digit_instructions: 'Say each digit out loud',
          challenge_turn_left: 'left',
          challenge_turn_right: 'right',
          challenge_turn_template: 'Look over your %{side} shoulder'
        },
        status: 'Recording'
      },
      video_confirmation: {
        title: 'Check selfie video',
        video_accessibility: 'Replay your recorded video'
      },
      video_intro: {
        button_primary: 'Continue',
        list_accessibility: 'Actions to record a selfie video',
        list_item_actions: 'We\'ll ask you to film yourself performing <strong>2 simple actions<\/strong>',
        list_item_speak: 'One will involve <strong>speaking out loud<\/strong>',
        title: 'Let\'s make sure nobody\'s impersonating you'
      },
      welcome: {
        description_p_1: 'You need to identify for your card',
        description_p_2: 'Please click the button below.',
        next_button: 'Proceed',
        title: 'Identification for your card'
      }
    };

    if ( lang == 'de') {
      obj = {
        country_select: {
          alert_dropdown: {
            country_not_found: 'Land nicht gefunden'
          },
          alert: {
            another_doc: 'Dokumente aus diesem Land werden derzeit nicht unterstützt. <fallback>Versuchen Sie es mit einem anderen Dokumententyp<\/fallback>'
          },
          button_primary: 'Dokument einreichen',
          search: {
            accessibility: 'Land auswählen',
            input_placeholder: 'z.B. Vereinigte Staaten',
            label: 'Suche nach Land'
          },
          title: 'Ausstellungsland auswählen'
        },
        cross_device_checklist: {
          button_primary: 'Bestätigung senden',
          info: 'Tipps',
          list_item_doc_multiple: 'Dokumente hochgeladen',
          list_item_doc_one: 'Dokument hochgeladen',
          list_item_selfie: 'Selfie hochgeladen',
          list_item_video: 'Video hochgeladen',
          subtitle: 'Wir sind jetzt bereit, Ihre Identität zu überprüfen.',
          title: 'Großartig, das ist alles, was wir brauchen.'
        },
        cross_device_error_desktop: {
          subtitle: 'Der Link funktioniert nur auf mobilen Geräten.',
          title: 'Etwas ist schiefgelaufen.'
        },
        cross_device_error_restart: {
          subtitle: 'Sie müssen Ihre Verifizierung auf Ihrem Computer neu starten.',
          title: 'Etwas ist schiefgelaufen.'
        },
        cross_device_intro: {
          button_primary: 'Sicherheitslink erhalten',
          list_accessibility: 'Erforderliche Schritte zur weiteren Verifizierung auf Ihrem Mobiltelefon',
          list_item_finish: 'Überprüfen Sie alle Eingaben und Daten, um die Einreichung abzuschließen.',
          list_item_open_link: 'Öffnen Sie den Link und führen Sie die Aktionen aus.',
          list_item_send_phone: 'Senden Sie einen Sicherheitslink an Ihr Mobiltelefon.',
          subtitle: 'So geht\'s:',
          title: 'Fahren Sie mit Ihrem Mobiltelefon fort.'
        },
        cross_device_return: {
          body: 'Die Aktualisierung Ihres Computers kann einige Sekunden dauern.',
          subtitle: 'Sie können nun zu Ihrem Computer zurückkehren, um fortzufahren.',
          title: 'Uploads erfolgreich!'
        },
        doc_confirmation: {
          alert: {
            blur_detail: 'Stellen Sie sicher, dass alles klar ist',
            blur_title: 'Unscharfes Foto erkannt',
            crop_detail: 'Stellen Sie sicher, dass das vollständige Dokument zu sehen ist',
            crop_title: 'Abgeschnittenes Bild erkannt',
            glare_detail: 'Versuchen Sie, sich von direktem Licht zu entfernen',
            glare_title: 'Spiegelung erkannt',
            no_doc_detail: 'Stellen Sie sicher, dass das gesamte Dokument auf dem Foto zu sehen ist.',
            no_doc_title: 'Kein Dokument erkannt.'
          },
          body_bank_statement: 'Make sure details are clear to read, with no blur or glare',
          body_benefits_letter: 'Make sure details are clear to read, with no blur or glare',
          body_bill: 'Make sure details are clear to read, with no blur or glare',
          body_id: 'Die Details der Karte müssen deutlich lesbar sein, ohne Unschärfe oder Spiegelung',
          body_image_medium: 'Es wird länger dauern, Sie zu verifizieren, wenn wir es nicht lesen können',
          body_image_poor: 'Um Sie reibungslos zu verifizieren, benötigen wir ein besseres Foto',
          body_license: 'Die Führerschein Details müssen deutlich lesbar sein, ohne Unschärfe oder Spiegelung',
          body_passport: 'Die Reisepass Details müssen deutlich lesbar sein, ohne Unschärfe oder Spiegelung',
          body_permit: 'Die Details der Erlaubnis müssen deutlich lesbar sein, ohne Unschärfe oder Spiegelung',
          body_tax_letter: 'Make sure details are clear to read, with no blur or glare',
          button_close: 'Schließen',
          button_primary_redo: 'Wiederholen',
          button_primary_upload: 'Bestätigen',
          button_primary_upload_anyway: 'Trotzdem hochladen',
          button_secondary_redo: 'Wiederholen',
          button_zoom: 'Bild vergrößern',
          image_accessibility: 'Foto Ihres Dokuments',
          title: 'Überprüfen Sie Ihr Bild'
        },
        doc_select: {
          button_bank_statement: 'Bank or building society statement',
          button_benefits_letter: 'Benefits Letter',
          button_benefits_letter_detail: 'Government authorised household benefits eg. Jobseeker allowance, Housing benefit, Tax credits',
          button_bill: 'Utility Bill',
          button_bill_detail: 'Gas, electricity, water, landline, or broadband',
          button_government_letter: 'Government Letter',
          button_government_letter_detail: 'Any government issued letter eg. Benefits entitlement, Voting letters, Tax letters, etc',
          button_id: 'Personalausweis',
          button_id_detail: 'Vorder- und Rückseite',
          button_license: 'Führerschein',
          button_license_detail: 'Vorder- und Rückseite',
          button_passport: 'Reisepass',
          button_passport_detail: 'Seite mit Foto',
          button_permit: 'Karte der Aufenthaltsgenehmigung',
          button_permit_detail: 'Vorder- und Rückseite',
          button_tax_letter: 'Council Tax Letter',
          extra_estatements_ok: 'e-statements accepted',
          extra_no_mobile: 'Sorry, no mobile phone bills',
          list_accessibility: 'Dokumente, die Sie zur Überprüfung Ihrer Identität verwenden können',
          subtitle: 'Es muss ein offizieller Lichtbildausweis sein',
          subtitle_poa: 'These are the documents most likely to show your current home address',
          title: 'Dokument auswählen',
          title_poa: 'Select a %{country} document'
        },
        doc_submit: {
          button_link_upload: 'oder Foto hochladen – keine Scans oder Kopien',
          button_primary: 'Weiter am Mobiltelefon',
          subtitle: 'Machen Sie ein Foto mit Ihrem Mobiltelefon.',
          title_bank_statement: 'Submit statement',
          title_benefits_letter: 'Submit letter',
          title_bill: 'Submit bill',
          title_government_letter: 'Government Letter',
          title_id_back: 'Personalausweis einreichen (Rückseite)',
          title_id_front: 'Personalausweis einreichen (Vorderseite)',
          title_license_back: 'Führerschein einreichen (Rückseite)',
          title_license_front: 'Führerschein einreichen (Vorderseite)',
          title_passport: 'Passfoto-Seite einreichen',
          title_permit_back: 'Aufenthaltsgenehmigung einreichen (Rückseite)',
          title_permit_front: 'Aufenthaltsgenehmigung einreichen (Vorderseite)',
          title_tax_letter: 'Submit letter'
        },
        error_unsupported_browser: {
          subtitle_android: 'Starten Sie den Prozess auf der neuesten Version von Google Chrome neu.',
          subtitle_ios: 'Starten Sie den Prozess mit der neuesten Version von Safari neu.',
          title_android: 'Nicht unterstützter Browser',
          title_ios: 'Nicht unterstützter Browser'
        },
        generic: {
          accessibility: {
            close_sdk_screen: 'Bildschirm zur Identitätsprüfung schließen',
            dismiss_alert: 'Alarm verwerfen'
          },
          back: 'zurück',
          close: 'Schließen',
          errors: {
            interrupted_flow_error: {
              instruction: 'Starten Sie den Prozess auf einem anderen Gerät neu.',
              message: 'Kamera nicht erkannt'
            },
            invalid_size: {
              instruction: 'Die Dateigröße muss unter 10 MB sein.',
              message: 'Dateigröße überschritten.'
            },
            invalid_type: {
              instruction: 'Versuchen Sie einen anderen Dateityp zu verwenden.',
              message: 'Datei nicht hochgeladen.'
            },
            lazy_loading: {
              message: 'Beim Laden der Komponente ist ein Fehler aufgetreten.'
            },
            multiple_faces: {
              instruction: 'Nur Ihr Gesicht kann im Selfie zu sehen sein.',
              message: 'Mehrere Gesichter gefunden'
            },
            no_face: {
              instruction: 'Ihr Gesicht muss im Selfie zu sehen sein.',
              message: 'Kein Gesicht gefunden'
            },
            request_error: {
              instruction: 'Bitte versuchen Sie es erneut.',
              message: 'Verbindung unterbrochen'
            },
            sms_failed: {
              instruction: 'Kopieren Sie den Link auf Ihr Mobiltelefon.',
              message: 'Etwas ist schiefgelaufen.'
            },
            sms_overuse: {
              instruction: 'Kopieren Sie den Link auf Ihr Mobiltelefon.',
              message: 'Zu viele fehlgeschlagene Versuche'
            },
            unsupported_file: {
              instruction: 'Versuchen Sie es mit einer JPG- oder PNG-Datei.',
              message: 'Dateityp nicht unterstützt.'
            }
          },
          lazy_load_placeholder: 'Wird geladen …',
          loading: 'Wird geladen'
        },
        get_link: {
          alert_wrong_number: 'Überprüfen Sie, ob Ihre Nummer korrekt ist.',
          button_copied: 'Kopiert',
          button_copy: 'Kopieren',
          button_submit: 'Link senden',
          info_qr_how: 'So scannen Sie einen QR-Code',
          info_qr_how_list_item_camera: 'Richten Sie die Kamera Ihres Telefons auf den QR-Code.',
          info_qr_how_list_item_download: 'Wenn es nicht funktioniert, laden Sie einen QR-Code-Scanner von Google Play oder aus dem App Store herunter.',
          link_divider: 'oder',
          link_qr: 'QR-Code scannen',
          link_sms: 'Link per SMS erhalten',
          link_url: 'Link kopieren',
          loader_sending: 'Senden',
          number_field_input_placeholder: 'Mobiltelefonnummer eingeben',
          number_field_label: 'Geben Sie Ihre Mobiltelefonnummer ein:',
          subtitle_qr: 'Scannen Sie den QR-Code mit Ihrem Mobiltelefon.',
          subtitle_sms: 'Senden Sie diesen einmaligen Link an Ihr Mobiltelefon.',
          subtitle_url: 'Öffnen Sie den Link auf Ihrem Mobiltelefon.',
          title: 'Holen Sie sich Ihren Sicherheitslink',
          url_field_label: 'Kopieren Sie den Link in Ihren mobilen Browser.'
        },
        linked_computer: {
          button_primary: 'Fortfahren',
          info: 'Stellen Sie sicher',
          list_item_desktop_open: '2. Ihr Desktop-Fenster bleibt geöffnet',
          list_item_sent_by_you: '1. Dieser Link wurde von Ihnen gesendet',
          subtitle: 'Fahren Sie mit der Überprüfung fort',
          title: 'Mit Ihrem Computer verbunden'
        },
        mobilePhrases: {
          photo_upload: {
            body_id_back: 'Machen Sie ein Foto von der Rückseite Ihrer Karte',
            body_id_front: 'Machen Sie ein Foto von der Vorderseite Ihrer Karte',
            body_license_back: 'Machen Sie ein Foto von der Rückseite Ihres Führerscheins',
            body_license_front: 'Machen Sie ein Foto von der Vorderseite Ihres Führerscheins',
            body_passport: 'Machen Sie ein Foto von Ihrer Passfotoseite',
            body_selfie: 'Machen Sie ein Selfie, das Ihr Gesicht zeigt'
          },
          selfie_capture: {
            alert: {
              camera_inactive: {
                detail: 'Fotografieren Sie stattdessen mit dem <fallback>einfachen Kameramodus<\/fallback>'
              },
              camera_not_working: {
                detail: 'Fotografieren Sie stattdessen mit dem <fallback>einfachen Kameramodus<\/fallback>'
              }
            }
          },
          upload_guide: {
            button_primary: 'Ein Foto machen',
            title: 'Reisepass Foto-Seite'
          }
        },
        outro: {
          body: 'Danke',
          title: 'Verifizierung abgeschlossen'
        },
        permission_recovery: {
          button_primary: 'Aktualisieren',
          info: 'Wiederherstellen',
          list_header_cam: 'Befolgen Sie diese Schritte, um den Kamerazugriff wiederherzustellen:',
          list_item_action_cam: 'Aktualisieren Sie diese Seite, um den Identitätsüberprüfungsprozess neu zu starten.',
          list_item_how_to_cam: 'Gewähren Sie den Zugriff auf Ihre Kamera über Ihre Browsereinstellungen.',
          subtitle_cam: 'Kamerazugriff wiederherstellen, um Gesichtsverifizierung fortzusetzen',
          title_cam: 'Kamerazugriff wird verweigert.'
        },
        permission: {
          body_cam: 'Wir können Sie nicht verifizieren, ohne Ihre Kamera zu benutzen.',
          button_primary_cam: 'Kamera aktivieren',
          subtitle_cam: 'Wenn Sie dazu aufgefordert werden, müssen Sie den Kamerazugriff aktivieren, um fortzufahren.',
          title_cam: 'Zugriff auf Kamera erlauben'
        },
        photo_upload: {
          body_bank_statement: 'Provide the whole document page for best results',
          body_benefits_letter: 'Provide the whole document page for best results',
          body_bill: 'Provide the whole document page for best results',
          body_government_letter: 'Provide the whole document page for best results',
          body_id_back: 'Kartenrückseite von Ihrem Computer hochladen',
          body_id_front: 'Kartenvorderseite von Ihrem Computer hochladen',
          body_license_back: 'Rückseite des Führerscheins von Ihrem Computer hochladen',
          body_license_front: 'Vorderseite des Führerscheins von Ihrem Computer hochladen',
          body_passport: 'Passfotoseite von Ihrem Computer hochladen',
          body_selfie: 'Selfie von Ihrem Computer hochladen',
          body_tax_letter: 'Provide the whole document page for best results',
          button_take_photo: 'Foto machen',
          button_upload: 'Hochladen',
          title_selfie: 'Selfie'
        },
        poa_guidance: {
          button_primary: 'Fortfahren',
          instructions: {
            address: 'Current address',
            full_name: 'Full name',
            issue_date: 'Issue date or summary period',
            label: 'Make sure it clearly shows:',
            logo: 'Logo'
          },
          subtitle_bank_statement: 'Must be issued in the <strong>last 3 months<\/strong>',
          subtitle_benefits_letter: 'Must be issued in the <strong>last 12 months<\/strong>',
          subtitle_bill: 'Must be issued in the <strong>last 3 months<\/strong>',
          subtitle_government_letter: 'Must be issued in the <strong>last 12 months<\/strong>',
          subtitle_tax_letter: 'Must be issued in the <strong>last 12 months<\/strong>'
        },
        poa_intro: {
          button_primary: 'Start verification',
          list_matches_signup: '<strong>Matches<\/strong> the address you used on signup',
          list_most_recent: 'Is your most <strong>recent<\/strong> document',
          list_shows_address: 'Shows your <strong>current<\/strong> address',
          subtitle: 'You\'ll need a document that:',
          title: 'Let\'s verify your %{country} address'
        },
        selfie_capture: {
          alert: {
            camera_inactive: {
              detail: 'Überprüfen Sie, ob die Kamera verbunden und funktionsfähig ist. Sie können <fallback>die Überprüfung auch auf Ihrem Telefon fortsetzen.<\/fallback>',
              detail_no_fallback: 'Stellen Sie sicher, dass Ihr Gerät über eine funktionierende Kamera verfügt.',
              title: 'Kamera funktioniert nicht?'
            },
            camera_not_working: {
              detail: 'Die Verbindung kann unterbrochen werden. <fallback>Versuchen Sie stattdessen, Ihr Telefon zu benutzen<\/fallback>.',
              detail_no_fallback: 'Stellen Sie sicher, dass die Kamera Ihres Geräts funktioniert.',
              title: 'Kamera funktioniert nicht.'
            },
            timeout: {
              detail: 'Denken Sie daran, Stopp zu drücken, wenn Sie fertig sind. <fallback>Video-Aktionen wiederholen<\/fallback>',
              title: 'Sieht aus, als hätten Sie zu lange gebraucht.'
            }
          },
          button_accessibility: 'Ein Foto machen',
          frame_accessibility: 'Ansicht von der Kamera',
          title: 'Ein Selfie aufnehmen'
        },
        selfie_confirmation: {
          image_accessibility: 'Foto von Ihrem Gesicht',
          subtitle: 'Stellen Sie sicher, dass Ihr Selfie Ihr Gesicht deutlich zeigt',
          title: 'Selfie prüfen'
        },
        selfie_intro: {
          button_primary: 'Fortfahren',
          list_accessibility: 'Tipps für ein gutes Selfie',
          list_item_face_forward: 'Richten Sie Ihr Gesicht nach vorne und stellen Sie sicher, dass Ihre Augen deutlich sichtbar sind.',
          list_item_no_glasses: 'Nehmen Sie Ihre Brille ab, falls erforderlich.',
          subtitle: 'Wir werden es mit Ihrem Dokument vergleichen.',
          title: 'Ein Selfie aufnehmen'
        },
        sms_sent: {
          info: 'Tipps',
          info_link_expire: 'Ihr Link läuft in einer Stunde ab.',
          info_link_window: 'Lassen Sie dieses Fenster geöffnet, während Sie Ihr Mobiltelefon bedienen.',
          link: 'Link erneut senden',
          subtitle: 'Wir haben einen sicheren Link an %{number} gesendet.',
          subtitle_minutes: 'Dieser Vorgang kann einige Minuten dauern.',
          title: 'Überprüfen Sie Ihr Mobiltelefon.'
        },
        switch_phone: {
          info: 'Tipps',
          info_link_expire: 'Ihr mobiler Link läuft in einer Stunde ab.',
          info_link_refresh: 'Diese Seite nicht aktualisieren.',
          info_link_window: 'Lassen Sie dieses Fenster geöffnet, während Sie Ihr Mobiltelefon bedienen.',
          link: 'Abbrechen',
          subtitle: 'Wenn Sie fertig sind, gelangen Sie zum nächsten Schritt.',
          title: 'Mit Ihrem Mobiltelefon verbunden'
        },
        upload_guide: {
          button_primary: 'Foto hochladen',
          image_detail_blur_alt: 'Beispiel eines verschwommenen Dokuments',
          image_detail_blur_label: 'Alle Details müssen klar sein - nichts verschwommen',
          image_detail_cutoff_alt: 'Beispiel eines abgeschnittenen Dokuments',
          image_detail_cutoff_label: 'Alle Details anzeigen - einschließlich der unteren 2 Zeilen',
          image_detail_glare_alt: 'Beispiel eines Dokuments mit Blendung',
          image_detail_glare_label: 'Entfernen Sie sich von direktem Licht - keine Blendung',
          image_detail_good_alt: 'Beispiel für ein Dokument',
          image_detail_good_label: 'Das Foto sollte Ihr Dokument deutlich zeigen',
          subtitle: 'Scans und Fotokopien werden nicht akzeptiert',
          title: 'Fotoseite des Reisepasses hochladen'
        },
        user_consent: {
          button_primary: 'Accept',
          button_secondary: 'Do not accept',
          prompt: {
            button_primary: 'Review again',
            button_secondary: 'Yes, don’t verify me',
            no_consent_detail: 'If you do not accept Onfido’s privacy statements and terms of service, we will not be able to verify your identity and you will exit this step.',
            no_consent_title: 'Are you sure?'
          }
        },
        video_capture: {
          body: 'Positionieren Sie Ihr Gesicht im Oval',
          body_next: 'Wenn Sie fertig sind, klicken Sie auf Weiter.',
          body_record: 'Drücken Sie auf Aufnahme und folgen Sie den Anweisungen.',
          body_stop: 'Wenn Sie fertig sind, drücken Sie Stopp.',
          button_primary_next: 'Weiter',
          button_record_accessibility: 'Aufnahme starten',
          button_stop_accessibility: 'Aufnahme stoppen',
          frame_accessibility: 'Ansicht von der Kamera',
          header: {
            challenge_digit_instructions: 'Sprechen Sie jede Ziffer laut aus.',
            challenge_turn_left: 'links',
            challenge_turn_right: 'rechts',
            challenge_turn_template: 'Schauen Sie über Ihre %{side} Schulter.'
          },
          status: 'Aufnahme'
        },
        video_confirmation: {
          title: 'Selfie-Video prüfen',
          video_accessibility: 'Wiedergeben Ihres aufgezeichneten Videos'
        },
        video_intro: {
          button_primary: 'Fortfahren',
          list_accessibility: 'Aktionen zum Aufzeichnen eines Selfie-Videos',
          list_item_actions: 'Wir bitten Sie, sich bei der Durchführung von <strong>zwei einfachen Handlungen<\/strong> zu filmen.',
          list_item_speak: 'Eine Aktion wird sein, <strong>etwas laut auszusprechen<\/strong>.',
          title: 'Lassen Sie uns sicherstellen, dass sich niemand für Sie ausgibt.'
        },
        welcome: {
          description_p_1: 'Um deine Kartenbestellung abzuschließen, müssen wir deine Identität überprüfen.',
          description_p_2: 'Es wird nur ein paar Minuten dauern.',
          next_button: 'Identität überprüfen',
          title: 'Kartenbestellung abschließen'
        }
      };

    }


    return obj;
  }

  ngOnInit() {

    let lng = navigator.language || 'en';
    if ( lng.substr(0,2) == 'de') {
      lng = 'de';
    } else {
      lng = 'en';
    }

    // return;

    this.protectedOrderId = this.route.snapshot.queryParamMap.get('orderId');
    const that = this;
    this.onfidoService.getWebSdkTokenForOrderId(this.protectedOrderId).subscribe((token) => {

      if ( token.startsWith('http') ) {
        window.location.href = token;
        return;
      }

      init({
        language: {
          locale: lng as SupportedLanguages,
          phrases: that.getPhrases(lng),
        },
        token,
        containerId: 'onfido-mount',
        onComplete(data) {
          that.onfidoService.doCheck(data, that.protectedOrderId).subscribe(() => {

          });
        },
        steps: [
          'welcome',
          {
            type: 'document',
            options: {
              documentTypes: {
                driving_licence: true,
                national_identity_card: true,
                passport: true,
                residence_permit: false
              }
            }
          },
          'document',
          'face',
          'complete'
        ]
      });
    });


  }

}
